import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Podcasts as PodcastsIcon, AccountCircle as AccountCircleIcon } from '@mui/icons-material';
import userStore from '../Stores/User';
import { isPaidProUser } from '../constants/plans';

const ProfileIcon = observer(({ inTopNav = false }) => {
  const location = useLocation();
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  
  // Check screen width to determine if we should hide the podcast button
  useEffect(() => {
    const checkScreenWidth = () => {
      setIsNarrowScreen(window.innerWidth < 400);
    };

    checkScreenWidth();
    window.addEventListener('resize', checkScreenWidth);
    return () => window.removeEventListener('resize', checkScreenWidth);
  }, []);

  // Only show ProfileIcon when TopNavBar is hidden (onboarding, share, feeds pages)
  // TopNavBar is hidden on: onboarding, share, feeds, feed pages
  const isTopNavBarHidden = location.pathname.startsWith('/onboarding/') || 
                           location.pathname.startsWith('/share/') || 
                           location.pathname === '/feeds' || 
                           location.pathname.startsWith('/feed/');
  
  // Hide the profile icon if we're on settings page, choosevoice page, or if TopNavBar is shown
  if (location.pathname === '/settings' || location.pathname === '/onboarding/choosevoice' || location.pathname === '/onboarding/ChooseVoice') {
    return null;
  }
  
  // Hide if TopNavBar is shown
  if (!isTopNavBarHidden) {
    return null;
  }

  const handleClick = () => {
    window.location.href = '/settings';
  };

  const handlePodcastClick = () => {
    window.location.href = '/setupnew';
  };

  const handleUpgradeClick = async () => {
    const hasStripeCustomerId = userStore.user?.stripe_customer_id || userStore.user?.customer_id;
    const hasActiveSubscription = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;
    if (hasStripeCustomerId && !hasActiveSubscription) {
      try {
        const response = await import('../audiblogsApi').then(m => m.createReactivationCheckoutSession(userStore.idToken, {}, '/add'));
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          window.location.href = '/onboarding/choosevoice';
        }
      } catch {
        window.location.href = '/onboarding/choosevoice';
      }
    } else {
      window.location.href = '/onboarding/choosevoice';
    }
  };

  // Check if user is pro
  const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
    ? isPaidProUser(userStore.user)
    : false;

  // Determine if podcast icon should be visible
  const isPodcastIconVisible = !isNarrowScreen && (isProUser || !userStore.user);

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      zIndex: 1001
    }}>
      {/* Upgrade button for non-pro users - only show when not in TopNavBar */}
      {!isProUser && userStore.user && !inTopNav && (
        <div
          onClick={handleUpgradeClick}
          style={{
            height: '36px',
            padding: '0 18px',
            borderRadius: '18px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '13px',
            color: '#fff',
            fontWeight: '600',
            letterSpacing: '0.5px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            whiteSpace: 'nowrap',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px) scale(1.02)';
            e.target.style.boxShadow = '0 8px 30px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
            e.target.style.background = 'linear-gradient(135deg, #7c8ff0 0%, #8a5bb8 50%, #f5a0ff 100%)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)';
          }}
          onMouseDown={(e) => {
            e.target.style.transform = 'translateY(1px) scale(0.98)';
          }}
          onMouseUp={(e) => {
            e.target.style.transform = 'translateY(-1px) scale(1.02)';
          }}
          title="Upgrade to Pro"
        >
          <span style={{ 
            marginRight: '4px', 
            fontSize: '12px',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
          }}>⭐</span>
          Upgrade
        </div>
      )}
      
      {/* Podcast icon - show on desktop when not in TopNavBar and user is pro or no user */}
      {isPodcastIconVisible && !inTopNav && (
        <div
          onClick={handlePodcastClick}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(24, 15, 34, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '18px',
            color: '#fff',
            fontWeight: 'bold',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.9)';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(24, 15, 34, 0.8)';
            e.target.style.transform = 'scale(1)';
          }}
          title="Podcast Setup"
        >
          <PodcastsIcon style={{ 
            fontSize: '20px', 
            backgroundColor: 'transparent', 
            display: 'block',
            margin: 0,
            padding: 0,
            lineHeight: 1
          }} />
        </div>
      )}
      
      {/* Profile icon */}
      <div
        onClick={handleClick}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: 'rgba(24, 15, 34, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '18px',
          color: '#fff',
          fontWeight: 'bold',
          transition: 'all 0.2s ease',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.8)';
          e.target.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'rgba(24, 15, 34, 0.8)';
          e.target.style.transform = 'scale(1)';
        }}
        title="Settings"
      >
        <AccountCircleIcon style={{ fontSize: '20px', color: '#fff' }} />
      </div>
    </div>
  );
});

export default ProfileIcon; 